
import { mergeMap, tap } from 'rxjs/operators';
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot } from "@angular/router";
import { Observable } from "rxjs";

import { LoaderService } from "../../common/services/loader.service";
import { WorkOrderService } from "../services/work-order.service";
import { FlowInstanceService } from "../../flow/services/flow-instance.service";
import { FlowInstance, ButtonPosition, QuestionKind, PageType, FileUploadQuestion } from "../../flow/models/flow.model";
import StaticPromise from "../../common/models/static-promise";
import { WorkOrderWithAssignments, ScanOrder } from "../models/work-order.model";

@Injectable()
export class ScanOrderResolver  {
    constructor(public readonly workOrderService: WorkOrderService, public readonly flowService: FlowInstanceService, public readonly loaderService: LoaderService) {
    }

    resolve(route: ActivatedRouteSnapshot): any | Observable<any> | Promise<any> {
        this.loaderService.toggleLoader(true);
        const workOrderId = route.paramMap.get("workOrderId");
        return this.workOrderService.getWorkOrder(workOrderId).pipe(mergeMap(wo => {
            return this.flowService.getByWorkItem(wo.flowInstanceId, wo.id).pipe(mergeMap(fi => {
                const scanOrder = this.transformScanOrder(wo, fi);
                return [scanOrder];
            }));
        }), tap((data) => { this.loaderService.toggleLoader(false); }));
    }

    public transformScanOrder(workOrder: WorkOrderWithAssignments, instance: FlowInstance): ScanOrder {
        const scanOrder = {
            workOrderId: workOrder.id,
            companyName: workOrder.companyName,
            description: workOrder.description,
            assignedUserId: workOrder.assignedUserId,
            flowInstance: instance,
            applicationNumber: workOrder.applicationNumber,
            status: workOrder.status,
            organizationId: (workOrder.organizationId) ? workOrder.organizationId : instance.organizationId,
            scanRequest: workOrder.scanRequest,
            uploads: []
        } as ScanOrder;
        const inputStates = instance.findInputForms();
        for (let s = 0; s < inputStates.length; s++) {
            if (inputStates[s].sections.length === 0) continue;
            const pageCollection = [];
            const nextButtons = [];
            for (let st = 0; st < inputStates[s].sections.length; st++) {
                for (let p = 0; p < inputStates[s].sections[st].pages.length; p++) {
                    pageCollection.push(inputStates[s].sections[st].pages[p]);
                    const right = inputStates[s].sections[st].pages[p].buttons.find(b => b.position === ButtonPosition.Right);
                    if (right) {
                        nextButtons.push(right);
                    }
                }
            }
            const buttonPromises = nextButtons.map(b => {
                if (b.targetPage) {
                    return b.targetPage();
                } else {
                    return StaticPromise.from<number>(() => -1);
                }
            });

            Promise.all(buttonPromises).then(destinations => {
                let pageList: number[] = [];
                let nextPageIndex = 0;
                do {
                    const rPage = pageCollection[nextPageIndex];
                    if (rPage.pageType !== PageType.PaperScanning) {
                        for (let r = 0; r < rPage.questions.length; r++) {
                            const row = rPage.questions[r];
                            const uploads = row.filter(q => q.type === QuestionKind.FileUpload)
                            for (let u = 0; u < uploads.length; u++) {
                                uploads[u].enabled = this.overrideRule(); //ensures that uploads that are hidden or disabled to the submitter are enabled and visible in the scan UI
                                uploads[u].visible = this.overrideRule()
                                let exists: boolean = (scanOrder.uploads.findIndex(up => up.contextTarget === uploads[u].contextTarget) !== -1)
                                                || (uploads.findIndex(up => up.contextTarget === uploads[u].contextTarget) !== -1);
                                if (!exists) {
                                    scanOrder.uploads.unshift(uploads[u] as FileUploadQuestion);
                                }
                                scanOrder.uploads.push(uploads[u]);
                            }
                        }
                    }
                    pageList.push(nextPageIndex);
                    nextPageIndex = destinations[nextPageIndex];
                } while (nextPageIndex > 0 && pageList.find(p => p === nextPageIndex) == null);

                if (instance.onBehalf) { //on behalf page should always be in the last section, so that's the only one care about
                    let scanPage = inputStates[s].sections[inputStates[s].sections.length - 1].pages.find(p => p.pageType === PageType.PaperScanning);
                    if (scanPage != null) {
                        scanPage.questions.forEach(r => {
                            let uploads = r.filter(q => q.type === QuestionKind.FileUpload);
                            uploads.forEach(u => {
                                let exists: boolean = scanOrder.uploads.findIndex(up => up.contextTarget === u.contextTarget) !== -1;
                                if (!exists) {
                                    if (scanOrder.scanRequest != null) {
                                        let docType = scanOrder.scanRequest.documentType;
                                        let config = u.config;
                                        if (u.config != null) {
                                            u.config["documentType"] = docType;
                                        }                                        
                                    }
                                    scanOrder.uploads.unshift(u as FileUploadQuestion);
                                }
                            });
                        })
                    }
                }
            });
        }
        return scanOrder;
    }

    private addPaperUpload(): void {

    }

    public overrideRule(): () => boolean {
        return (): boolean => { return true; };
    }
}